import {AppState} from "../RootReducer";
import {
    CasinoCurrencies,
    CasinoGameInfo,
    CasinoGameInfo as CasinoGame,
    CasinoLobby,
    CasinoLobbyCategory,
    CasinoLobbyCategoryType,
    CasinoPage,
    CasinoPageTab,
    CasinoStudio,
    CentralImagesConfig,
    CurrencyDefinition,
    GameTag,
    Jackpot,
    Settings
} from "../http/protocol";
import {createSelector} from "reselect";
import {getActiveCurrency, getActiveCurrencyCode, getCountry, getLoggedIn, getUserAttributes} from "./user-selectors";
import {Device} from "../utils/Device";

import {Banner, BannerCarousel, BaseContent, CasinoBannerPayload, GameListSection} from "../contenful/types";
import {
    CategoriesByPage,
    extractGamesByCategory,
    extractStudios,
    gameInPage,
    isGameAvailable,
    PageStudio
} from "../casino/CasinoActions";
import {DynamicContentEnum} from "../landingpage/pagesection/DynamicContentView";
import {CasinoState} from "../casino/CasinoReducer";
import {
    getCountry as getSettingsCountry,
    getCurrencyDefinitions,
    getJurisdiction,
    getSettings
} from "./settings-selectors";
import CurrencyFormatter from "../utils/CurrencyFormatter";
import {getJackpots} from "./shared-selectors";
import {getAllCasinoBonusGameIds} from "./bonus-selectors";


export const getCasinoState = (app : AppState) => app.casino;
export const isSupportedPlatform = (g? : CasinoGame) => {
    if(g) {
        if(Device.isMobile() && g.mobileExternalId) {
            return true;
        } else if(!Device.isMobile() && g.externalId) {
            return true;
        }
        return false;
    }
    return false;
}



export const isSupportedCurrency = (g?: CasinoGame, casinoCurrencies? : CasinoCurrencies[], activeCurrency?:string, loggedIn?:boolean) => {

    if(!casinoCurrencies || !g) {
        return false;
    }

    if(!activeCurrency || !loggedIn) {
        return true;
    }

    if(loggedIn === undefined) {
        return true;
    }

    let c : CasinoCurrencies | undefined = casinoCurrencies.find( c => c.studioId == g.studioId);
    if(c && c.currencies) {
        let allowed = c.currencies.find(currency => currency == activeCurrency);
        if(allowed) {
            return true;
        }

    }
    return false;
}

export function isStudioAllowed(s?: CasinoStudio, country?: string) {
    if(!country  || country.trim().length == 0) {
        return true;
    }
    if(!s) {
        return true;
    }
    if(s.allowedCountries && s.allowedCountries.trim().length > 0) {
        let codes = s.allowedCountries.split(",");
        return codes.some( c => c.trim().toLowerCase() == country.toLowerCase() );
    }
    if(s.blockedCountries && s.blockedCountries.trim().length > 0) {
        let codes = s.blockedCountries.split(",");
        return !codes.some( c => c.trim().toLowerCase() == country.toLowerCase());
    }
    return true;
}

export function isGameAllowed(g?: CasinoGame, s?: CasinoStudio, country?: string) {
    if(!country  || country.trim().length == 0) {
        return true;
    }
    // First check settings on game level
    if(!g) {
        return true;
    }
    if(g.allowedCountries && g.allowedCountries.trim().length > 0) {
        let codes = g.allowedCountries.split(",");
        return codes.some( c => c.trim().toLowerCase() == country.toLowerCase() );
    }
    if(g.blockedCountries && g.blockedCountries.trim().length > 0) {
        let codes = g.blockedCountries.split(",");
        return !codes.some( c => c.trim().toLowerCase() == country.toLowerCase());
    }
    // If there are no game level settings, check settings on studio level
    if(!s) {
        return true;
    }
    if(s.allowedCountries && s.allowedCountries.trim().length > 0) {
        let codes = s.allowedCountries.split(",");
        return codes.some( c => c.trim().toLowerCase() == country.toLowerCase() );
    }
    if(s.blockedCountries && s.blockedCountries.trim().length > 0) {
        let codes = s.blockedCountries.split(",");
        return !codes.some( c => c.trim().toLowerCase() == country.toLowerCase());
    }
    return true;
}

export const getSearchValue = (state : AppState) => state.casino?.searchValue;

export const getSelectedTabId = (state : AppState) => state.casino?.selectedTabId;



export const getLobby = (state : AppState) => state.casino.lobby;

export const getFavouritesByPage = (state : AppState) => state.casino.favouriteGamesByPage ?? [];

export const getDefaultPage = (state : AppState) => state.casino.lobby?.pages?.find(p => p.defaultPage) ?? undefined;


export const getSelectedPage = (state : AppState) : CasinoPage | undefined => state.casino.lobby?.pages?.find(p => p.id == state.casino.selectedPageId) ?? undefined;
export const getSelectedPageName = createSelector([getSelectedPage, getDefaultPage],(selectedPage, defaultPage)=>{
    if(selectedPage) {
        return selectedPage.name;
    } else if(defaultPage) {
        return defaultPage.name;
    } else {
        return "Games";
    }
})
export const getGames = (app : AppState) => app.casino.lobby?.games ?? {};
export const getStudios = (state : AppState) => Object.values(state.casino.lobby?.studios ?? {});

const isStudioInPage = (s : CasinoStudio, p? : CasinoPage, studios? : PageStudio[], mobile?: boolean) => {
    if(!p || !studios) {
        return false;
    }
    let pageStudio = studios.find(s => s.id === p.id);
    if(pageStudio) {
        return !!pageStudio?.studios?.find(studioId => studioId === s.id);
    }
    return false;
}
const isTagInPage = (t : GameTag, p? : CasinoPage, games? : CasinoGameInfo[], mobile?: boolean) => {
    if(!p || !games) {
        return false;
    }
    return games.find(g => g.gameTags?.indexOf(t.id ?? 0) != -1 &&  gameInPage(g, p, mobile));
}
export const getStudiosInOrder = createSelector([getStudios, getCountry, getSelectedPage, getCasinoState, getSettingsCountry],
    (studios, country, selectedPage, state, settingsCountry) => {
    let finalCountry = country;
    if(!finalCountry) {
        finalCountry = settingsCountry;
    }
    studios = studios.filter(s => isStudioAllowed(s, finalCountry) && isStudioInPage(s, selectedPage, state.studiosInPage, Device.isMobile()))
    studios?.sort((a, b)=> (a?.name ?? "").localeCompare(b?.name ?? "") );
    return studios;
});
const _getSelectedPageId = (state : AppState) : number | undefined => state.casino.selectedPageId;

export const getPages = (state : AppState) : CasinoPage[] => state.casino.lobby?.pages ?? [];
export const getSelectedPageId = createSelector([getPages, _getSelectedPageId],(pages, selectedPageId) => {
    return selectedPageId ?? pages?.find(p => p.defaultPage)?.id ?? undefined;
})

export const getTags = (state : AppState) => Object.values(state.casino.lobby?.tags ?? {});

export function findTagsByPage(
    lobby?: CasinoLobby,
    gamesByCategory? : CategoriesByPage[],
    selectedPage?: CasinoPage,
    activeCurrency?: string,
    loggedIn?: boolean,
    country?: string,
    tags?: GameTag[]) : GameTag[] {
    let gamesInPage = (gamesByCategory ?? []).find(p => p.id == selectedPage?.id)?.categories?.flatMap(c => c.games) ?? [];

    return tags?.filter(tag => tag.display)
        .filter(tag => gamesInPage.some(g => g.gameTags?.some((tagId : number) => tag.id == tagId))) ?? [];
}



export const isSearching = (state : AppState) => (state.casino.searchValue && state.casino.searchValue.length > 0) ? true :  false;
export const getSelectedCategoryId = (state : AppState) => state.casino.selectedCategoryId;

export interface CasinoStudioLink {
    studio? : CasinoStudio;
    game?: CasinoGameInfo;
}
export interface CategoryStudios {
    categoryId? : number;
    links? : CasinoStudioLink[];
}
export interface GameState {
    games : CasinoGame[][];
    categories : CasinoLobbyCategory[];
    favourites : CasinoGame[];
    recentlyPlayed : CasinoGame[];
    currentTab? : CasinoPageTab;
    categoryStudios? : CategoryStudios[];
    availableStudios: CasinoStudio[];
    restrictedStudios : CasinoStudio[];
    availableTags : GameTag[];
    images? : CentralImagesConfig;
    userFavouritedgames : CasinoGame[];
}

export function extractCategoryTitle(cat?: CasinoPageTab) {
    return cat?.name ?? "";
}
export const isDefaultPage = (app : AppState) : boolean => {
    const defaultId = app.casino.lobby?.pages?.find(p => p.defaultPage)?.id ?? -1;
    return  defaultId === app.casino.selectedPageId;
}

export const getSelectedCategory = createSelector([getSelectedCategoryId, getPages], (selectedCategoryId, pages) => {
    return findCategory(pages, selectedCategoryId);
})



export const getFavouritesBySelectedPage = createSelector([getFavouritesByPage, getSelectedPage, getLobby],(favouritesByPage, selectedPage, lobby)=>{
    let ids = favouritesByPage?.find(p => p?.pageId == selectedPage?.id)?.ids ?? [];
    return ids.map(id => lobby?.games?.["" + id] ?? {});
})
export const getPageTabs = createSelector([getPages, _getSelectedPageId], (pages, selectedPageId) => {
    return pages.find(p => p.id == selectedPageId)?.tabs ?? [];
})

export const getSelectedPageTab = createSelector( [getSelectedTabId, getPageTabs],(selectedTabId, tabs)=>{
    return tabs?.find(t => t.id == selectedTabId);
});



export const getSurpriseEnabled = createSelector(
    [getSelectedPageTab],
    (tab)=>{
        return false;
    });


export const findCategory = (pages?: CasinoPage[], catId? : number)  => {
    if(!pages || typeof(catId) == "undefined") {
        return undefined;
    }
    for(let i = 0 ; i<pages.length; i++) {
        let tabs = pages[i].tabs ?? [];
        for(let j = 0; j<tabs.length; j++ ) {
            let categories = tabs[j].categories ?? [];
            let category = categories.find(c => c.id == catId);
            if(category) {
               return category;
            }
        }
    }
    return undefined;
}

export const getIsFilterActiveBonusGames = (state : AppState) : boolean | undefined => state.casino.isFilterActiveBonusGames;

export const getRecentlyPlayedGamesByPage = (app : AppState) => app.casino.recentlyPlayedGamesByPage ?? []

function extractGamesToRender(games: { [index: string]: CasinoGameInfo }, gameIds : number[], maxNrOfGames: number,
                              currencies: CasinoCurrencies[], activeCurrency?: string, loggedIn?: boolean,
                              cs? : CasinoStudio[], country? : string, showAll?: boolean, hideRestricted? : boolean) {
    let filtered = [];
    if(maxNrOfGames <= 0) {
        maxNrOfGames = MAX_NR_OF_GAMES_RENDERED;
    }
    for(let i = 0; i<gameIds.length; i++) {
        let game = games[gameIds[i]];
        if(!game) {
            continue;
        }
        if(includeGame(game,cs, country, currencies, activeCurrency, loggedIn, hideRestricted)) {
            filtered.push(game);
        }
        if(!showAll && filtered.length > maxNrOfGames) {
            break;
        }
    }
    return filtered;
}

function filterLobbyGamesOnIds(games: { [index: string]: CasinoGameInfo }, ids: number[]): { [index: string]: CasinoGameInfo } {
    const filteredGames: { [index: string]: CasinoGameInfo } = {};

    for (const key in games) {
        if (ids.includes(Number(key))) {
            const game = games[key];
            if (typeof game === 'object') {
                filteredGames[key] = game;
            }
        }
    }

    return filteredGames;
}

function filterGamesOnCasinoBonus(lobbyGames: { [index: string]: CasinoGameInfo }, favouriteIds: number[], recentlyPlayedIds: number[], selectedGames: CasinoGameInfo[], ids: number[]) {
    return {
        lobbyGames: filterLobbyGamesOnIds(lobbyGames, ids),
        favouritesByPage: favouriteIds.filter(id => ids.includes(id)),
        recentlyPlayedByPage: recentlyPlayedIds.filter(id => ids.includes(id)),
        selectedGames: selectedGames.filter(game => ids.includes(game?.id as number))
    }
}

export function isGameAllowedByJurisdictions(game: CasinoGameInfo, userJurisdictionId: string): boolean {
    if (!game.jurisdictions || game.jurisdictions.length === 0) {
        return true;
    }
    if (game.jurisdictions.includes(1)) {
        return true;
    }
    return game.jurisdictions.some(jurisdictionId => jurisdictionId.toString() === userJurisdictionId);
}

export const getSelectedGames = (app : AppState) => app.casino.selectedGames;
export const MAX_NR_OF_GAMES_RENDERED = 50;
export const getFilteredGames = (selectedStudios: number[], selectedTags: number[]) => createSelector(
    [
        getLobby,
        getSelectedTabId,
        getSelectedPageId,
        getSelectedCategoryId,
        getFavouritesByPage,
        getRecentlyPlayedGamesByPage,
        getActiveCurrencyCode,
        getLoggedIn,
        getCountry,
        getSettingsCountry,
        getJurisdiction,
        getSelectedGames,
        getSearchValue,
        getIsFilterActiveBonusGames,
        getAllCasinoBonusGameIds,
        getLoggedIn
    ],
    (

        lobby,
        selectedTabId,
        selectedPageId,
        selectedCategoryId,
        favouritesByPage,
        recentlyPlayedByPage,
        activeCurrency,
        loggedIn,
        country,
        settingsCountry,
        jurisdíctionId,
        selectedGames,
        searchValue,
        isFilterActiveBonusGames,
        allCasinoBonusGameIds,
        isLoggedIn,
    )  => {

        if (!selectedTags) {
            selectedTags = [];
        }
        if(!selectedStudios) {
            selectedStudios = [];
        }
        let games : CasinoGame[][] = [];
        let displayFavourites = false;
        let pages = lobby?.pages ?? [];
        let tab : CasinoPageTab | undefined;

        if (isFilterActiveBonusGames && isLoggedIn) {
            const filteredLists = filterGamesOnCasinoBonus(lobby?.games ?? {}, favouritesByPage[0]?.ids ?? [], recentlyPlayedByPage[0]?.ids ?? [], selectedGames ?? [], allCasinoBonusGameIds);

            if (lobby != null) {
                const { lobbyGames } = filteredLists;
                lobby = { ...lobby, games: lobbyGames };
            }
            favouritesByPage = [
                { ...favouritesByPage[0], ids: filteredLists.favouritesByPage },
                ...favouritesByPage.slice(1)
            ];
            recentlyPlayedByPage = [
                { ...recentlyPlayedByPage[0], ids: filteredLists.recentlyPlayedByPage },
                ...recentlyPlayedByPage.slice(1)
            ];
            selectedGames = filteredLists.selectedGames;
        }

        if(!country) {
            country = settingsCountry;
        }
        let selectedPage = pages.find(p => p.id == selectedPageId);
        if(!selectedPage?.tabs?.find(t => t.id == selectedTabId)) {
            selectedTabId  = selectedPage?.tabs?.[0]?.id;
        }
        if(!selectedPage) {
            if(typeof(selectedCategoryId) != "undefined") {
                let p = pages.find(p => p.tabs?.find(t => t.categories?.find(c => c.id == selectedCategoryId)));
                selectedPageId = p?.id;
            } else {
                selectedPageId = pages.find(p => p.defaultPage === true)?.id ?? -1;

            }
        }

        let tabs = selectedPage?.tabs ?? [];

        let showSelectedCategory = typeof(selectedCategoryId) != "undefined";

        let studios = selectedPage?.studios?.map(id => lobby?.studios?.["" + id] ?? {}) ?? [];
        if(showSelectedCategory) {
            let filtered : CasinoGameInfo[] = [];
            let category = findCategory(lobby?.pages, selectedCategoryId);
            if(category) {
                let allGamesInCategory = category.games;
                filtered = extractGamesToRender(
                    lobby?.games ?? {},
                    allGamesInCategory ?? [],
                    -1,
                    lobby?.currencies ?? [],
                    activeCurrency,
                    loggedIn,
                    studios,
                    country,
                    true
                    ).filter(game => isGameAllowedByJurisdictions(game, jurisdíctionId ?? ""));
            } else {
                filtered = [];
            }
            games = [filtered];

        } else if(selectedStudios?.length > 0 || selectedTags?.length > 0 || (searchValue && searchValue.length)) {
            games = selectedGames ? [selectedGames] : [];
        } else {
            if(tabs.length > 0 && tabs[0].id == selectedTabId) {
                displayFavourites = true;
            }
            tab = tabs.find(t => t.id == selectedTabId);
            if(tab && tab.categories && tab.categories.length > 0) {

                games = tab?.categories?.map(c => {
                    let allGamesInCategory = c.games;
                    let renderGamesInCategory = extractGamesToRender(
                        lobby?.games ?? {},
                        allGamesInCategory ?? [],
                        c.maxNrOfGames ?? -1,
                        lobby?.currencies ?? [],
                        activeCurrency,
                        loggedIn,
                        studios,
                        country,
                        false,
                        selectedPage?.hideCountryRestrictedGames)
                        .filter(game => isGameAllowedByJurisdictions(game, jurisdíctionId ?? ""));
                    return renderGamesInCategory ?? []
                }) ?? [];
            }
        }

        let favouritesToDisplay : CasinoGameInfo[] = [];
        let recentlyToDisplay : CasinoGameInfo[] = [];
        let allStudios = Object.values(lobby?.studios ?? {});
        let favGames = favouritesByPage.find(f => f.pageId === selectedPageId)?.ids?.map(id => lobby?.games?.["" + id] ?? {}) ?? [];
        if(displayFavourites) {
            favouritesToDisplay = favGames.filter(g =>  includeGame(g, allStudios, country,lobby?.currencies ?? [], activeCurrency, loggedIn,true)) ?? [];
            let recentGames = recentlyPlayedByPage.find(f => f.pageId == selectedPageId)?.ids?.map(id => lobby?.games?.["" + id] ?? {}) ?? [];
            recentlyToDisplay = recentGames.filter(g =>  includeGame(g, allStudios, country,lobby?.currencies ?? [], activeCurrency, loggedIn,true))?? [];
        }

        let categoryStudios = tab?.categories?.filter(c => c.categoryType === CasinoLobbyCategoryType.STUDIO_SELECTION)
            .map((c) => {
                let links = extractStudios(c, allStudios, c.games?.map( g => lobby?.games?.["" + g] ?? {})  ?? [])
                return {
                    categoryId : c.id,
                    links : links?.filter(cs => {
                        if(cs.game && includeGame(cs.game, allStudios, country,lobby?.currencies ?? [], activeCurrency, loggedIn, selectedPage?.hideCountryRestrictedGames)) {
                            return true;
                        } else if(isStudioAllowed(cs.studio, country)) {
                            return true;
                        }
                        return false;
                    })
                }
            })



        studios = studios.sort((a,b) => {
            return (a.name ?? "").localeCompare(b.name ?? "");
        })
        let tags = selectedPage?.gameTags?.map(id => lobby?.tags?.["" + id] ?? {}).filter(t => t.display === true) ?? [];
        tags = tags.sort((a,b) => {
            return (a.name ?? "").localeCompare(b.name ?? "");
        });

        let gameState : GameState = {
            games : games,
            favourites : favouritesToDisplay,
            recentlyPlayed : recentlyToDisplay,
            currentTab : tab,
            categories : tab?.categories ?? [],
            categoryStudios : categoryStudios,
            availableStudios : studios?.filter(s => isStudioAllowed(s, country)) ?? [],
            restrictedStudios : !selectedPage?.hideCountryRestrictedGames  ?  studios?.filter(s => !isStudioAllowed(s, country)) ?? [] : [],
            availableTags : tags,
            images : lobby?.images,
            userFavouritedgames: favGames
        }

        return gameState;
    }
)

export const includeGame = (g : CasinoGameInfo, cs? : CasinoStudio[], country? : string,
                            currencies? : CasinoCurrencies[], activeCurrency?  : string, loggedIn? : boolean,
                            hideRestricted? : boolean) =>{
    let gameAllowed = isGameAllowed(g, (cs ?? []).find(s => s.id === g?.studioId), country);
    if( (hideRestricted && !loggedIn && !gameAllowed) || (loggedIn && !gameAllowed)) {
        return false;
    } else if(!isGameAvailable(g, currencies ?? [], activeCurrency, loggedIn)) {
        return false
    }
    return true;
}

export const getVisibleStudios = createSelector(
    [getStudios, getCountry],
    (studios, country) => {
        return studios?.filter(s => isStudioAllowed(s, country));
    }
);

export const getSearchGameResults = createSelector(
    [getLobby,  getActiveCurrencyCode, getLoggedIn, getCountry, getSearchValue, getJurisdiction],
    (lobby, activeCurrency, loggedIn, country, searchValue, jurisdictionId) : CasinoGame[] =>{
        let games : CasinoGame[] = [];
        let studios = Object.values(lobby?.studios ?? {});
        if(!searchValue || searchValue.trim().length <= 1) {
            return games;
        }

        let allGames = Object.values(lobby?.games ?? {});
        allGames = allGames.filter((g : CasinoGame) => {
            if(!isSupportedPlatform(g)) {
                return false;
            }
            if(!isSupportedCurrency(g, lobby?.currencies ?? [], activeCurrency, loggedIn)) {
                return false;
            }
            if(!isGameAllowed(g, studios.find(s => s.id === g.studioId), country)) {
                return false;
            }
            if(!isGameAllowedByJurisdictions(g, jurisdictionId ?? "")) {
                return false;
            }
            return true;
        });

        games = allGames.filter(g => g.name && g.name.toLocaleLowerCase().indexOf(searchValue.toLocaleLowerCase()) != -1);

        return games.slice(0,100);
    }
);

export const getCasinoGames = (state : AppState) => state.casino.lobby?.games;

export const getCasinoBanners = (state : AppState) => state.contentful.casinoBanner;
export const getCasinoPages = (state: AppState) => state.contentful.casinoPage;

export interface CasinoGameBanners {
    casinoPageId?: number;
    banners : CasinoBannerPayload[];
    bannerGames : CasinoGame[];
}

const getId = (_:any, id : number) => id;

export const getTabsByPageId = createSelector([getPages, getId],(pages, id : number)=>{
    return pages.find(p => p.id == id)?.tabs ?? [];
});

const getJackpotId = (_:any, id : string) => id;
const getCurrency = (_:any, code? : string) => code;




export const findFiatCurrency = (code? : string, currencies? : CurrencyDefinition[]) : CurrencyDefinition | undefined => {

    if(!code || !currencies) {
        return undefined;
    }
    for(let i = 0; i<currencies.length; i++) {
        if(currencies[i].cryptoPrices?.[code]) {
            return currencies[i].cryptoPrices?.[code].currencyDefinition;
        }
    }
    return undefined;
}
export const getJackpotById = (jackpots : Jackpot[], settings? : Settings, currencies? : CurrencyDefinition[], activeCurrency?  : CurrencyDefinition, userAttributes? : any, id? : string, jackpotCurrency? : string)=>{
    let jackpot : Jackpot | undefined = jackpots.find(j => j.externalId === id);
    if(!jackpot) {
        return undefined;
    }
    if(!currencies) {
        currencies =[];
    }

    let currency : CurrencyDefinition | undefined = undefined;

    //if we have an active currency
    if(jackpotCurrency) {
        currency = currencies.find(c => c.code === jackpotCurrency);
        if(!currency) {
            currency = findFiatCurrency(jackpotCurrency, currencies);
        }
    } else if(activeCurrency) {
        //if crypto
        if(activeCurrency.cryptoCurrency == true) {
            if(userAttributes["preferredFiatExchangeCurrency"]) {
                currency =  findFiatCurrency(userAttributes["preferredFiatExchangeCurrency"], currencies);
            }
            if(!currency) {
                currency = findFiatCurrency(settings?.defaultFiatExchangeCurrency, currencies);
            }
        } else {
            currency = activeCurrency;
        }
    } else {
        if(currencies.length > 0) {
            if(currencies[0].cryptoCurrency == true) {
                currency = findFiatCurrency(settings?.defaultFiatExchangeCurrency, currencies);
            }  else {
                currency = currencies[0];
            }
        }
    }

    if(jackpot && currency) {

        let balances = jackpot.pools?.map(p => p.pool?.find( b => b.currencyCode === currency?.code)?.amount ?? 0);

        if(balances) {
            let max = Math.max(...balances);
            if(max != 0) {
                return  { formattedAmount : CurrencyFormatter.jackpotFormat(currency,max), amount : max, currency : currency }
            }
        }
    }
    return undefined;

}
export const getJackpotAmountByGame = createSelector([getJackpots, getSettings, getCurrencyDefinitions, getActiveCurrency,getUserAttributes, getId],
        (jackpots, settings, currencies, activeCurrency,userAttributes, id)=>{
    let jackpot : Jackpot | undefined = jackpots.find(j => j.games?.find(g => g.id === id) ?? false);
    let currency : CurrencyDefinition | undefined = undefined;

    //if we have an active currency
    if(activeCurrency) {
        //if crypto
        if(activeCurrency.cryptoCurrency == true) {
           if(userAttributes["preferredFiatExchangeCurrency"]) {
               currency =  findFiatCurrency(userAttributes["preferredFiatExchangeCurrency"], currencies);
           }
           if(!currency) {
               currency = findFiatCurrency(settings?.defaultFiatExchangeCurrency, currencies);
           }
        } else {
            currency = activeCurrency;
        }
    } else {
        if(currencies.length > 0) {
            if(currencies[0].cryptoCurrency == true) {
                currency = findFiatCurrency(settings?.defaultFiatExchangeCurrency, currencies);
            }  else {
                currency = currencies[0];
            }
        }
    }

    if(jackpot && currency) {
        let balances = jackpot.pools?.map(p => p.pool?.find( b => b.currencyCode === currency?.code)?.amount ?? 0);
        if(balances) {
            let max = Math.max(...balances);
            if(max != 0) {
                return  CurrencyFormatter.jackpotFormat(currency,max);
            }
        }
    }
    return undefined;
})

export const getCasinoGameById = createSelector([getCasinoGames,getId], (games, id) => {
    return games?.["" + id];
})


export const getDefaultPageId = (state : AppState) => state.casino.lobby?.pages?.find(p => p.defaultPage)?.id ?? undefined;

export interface BannerJackpotAmount { id: string, formattedAmount?: string, jackpotCurrency? : string, amount?: number, currency? : CurrencyDefinition}

export const getCasinoGameBanners = createSelector([getCasinoPages, getCountry, getGames, _getSelectedPageId, getDefaultPageId, getJackpots, getSettings, getCurrencyDefinitions, getActiveCurrency, getUserAttributes],
    (pages, country, games, selectedPageId, defaultCasinoPageId, jackpots, settings, currencies, activeCurrency, userAttributes)=>{

        if(!pages) {
            return undefined;
        }

        let page = pages.find(p => p.casinoPageId == selectedPageId);
        if(!page) {
            page = pages.find(p => p.casinoPageId == defaultCasinoPageId);
        }


        let casinoBanners = page?.banners ?? [];

        let banners = casinoBanners?.filter((banner : CasinoBannerPayload) => {
            let geoCode = country?.toLowerCase() || "";
            geoCode = geoCode.toLocaleLowerCase();
            //remove excluded banners
            let excludedCountries : string[] = [];
            if(banner.excludedCountries && Array.isArray(banner.excludedCountries)) {
                excludedCountries = banner.excludedCountries;
            } else if(typeof banner.excludedCountries === "string") {
                excludedCountries = banner.excludedCountries?.split(",") ?? [];
            }

            let includedCountries  : string[] = [];
            if(banner.includedCountries && Array.isArray(banner.includedCountries)) {
                includedCountries = banner.includedCountries;
            } else if(typeof banner.includedCountries === "string"){
                includedCountries = banner.includedCountries?.split(",") ?? [];
            }

            const excluded = excludedCountries && excludedCountries.map((b) => b.toLowerCase());
            const included = includedCountries && includedCountries.map((b) => b.toLowerCase());
            if(excluded && excluded.indexOf(geoCode) != -1) {
                return false;
            }
            //if includedCountries have items but the geo code is not in the array filter out the banner
            if(included && included.length > 0 && included.indexOf(geoCode) == -1) {
                return false;
            }

            return true;
        });

        let bannerGames = banners?.filter(b => !!games?.["" + b.gameId]).map(b => games?.["" + b.gameId]);
        let formattedJackpots : BannerJackpotAmount[] = [];
        banners.forEach(b => {
            if(b.jackpotId) {
                let jp = getJackpotById(jackpots, settings, currencies, activeCurrency, userAttributes, b.jackpotId, b.jackpotCurrency);
                if(jp) {
                    formattedJackpots.push({
                        id : b.jackpotId,
                        formattedAmount : jp?.formattedAmount,
                        jackpotCurrency : b.jackpotCurrency,
                        amount : jp?.amount,
                        currency : jp?.currency
                    })
                }
            }
        })

        return {
            casinoPageId : page?.casinoPageId ?? undefined,
            banners :banners,
            bannerGames : bannerGames,
            jackpots : formattedJackpots

        }
    })

export const getCarouselBanner = (_:any, cb : BannerCarousel ) => cb;

export const getGamesFromBanners = createSelector([getCarouselBanner, getCountry, getGames, _getSelectedPageId, getDefaultPageId, getJackpots, getSettings, getCurrencyDefinitions, getActiveCurrency, getUserAttributes],
    (bc, country, games, selectedPageId, defaultCasinoPageId, jackpots, settings, currencies, activeCurrency, userAttributes)=>{

    let casinoBanners = bc?.banners ?? [];

    let banners = casinoBanners?.filter((banner : Banner) => {

        let geoCode = country?.toLowerCase() || "";
        geoCode = geoCode.toLocaleLowerCase();

        let excludedCountries : string[] = [];
        if(banner.excludedCountries && Array.isArray(banner.excludedCountries)) {
            excludedCountries = banner.excludedCountries;
        } else if(typeof banner.excludedCountries === "string"){
            excludedCountries = banner.excludedCountries?.split(",") ?? [];
        }
        let includedCountries : string[] = [];
        if(banner.includedCountries && Array.isArray(banner.includedCountries)) {
            includedCountries = banner.includedCountries;
        } else if(typeof banner.includedCountries === "string"){
            includedCountries = banner.includedCountries?.split(",") ?? [];
        }

        //remove excluded banners
        const excluded = excludedCountries.map((b) => b.toLowerCase());
        const included = includedCountries.map((b) => b.toLowerCase());
        if(excluded && excluded.indexOf(geoCode) != -1) {
            return false;
        }
        //if includedCountries have items but the geo code is not in the array filter out the banner
        if(included && included.length > 0 && included.indexOf(geoCode) == -1) {
            return false;
        }
        return true;
    });

    let bannerGames = banners?.filter(b => !!games?.["" + b.gameId]).map(b => games?.["" + b.gameId]);
    banners = banners.filter(b => !b.gameId || !!bannerGames.find(bg => bg.id == b.gameId));

    let formattedJackpots : BannerJackpotAmount[] = [];
    banners.forEach(b => {
        if(b.jackpotId) {
            let jp = getJackpotById(jackpots, settings, currencies, activeCurrency, userAttributes, b.jackpotId, b.jackpotCurrency);
            if(jp) {
                formattedJackpots.push({
                    id : b.jackpotId,
                    formattedAmount : jp?.formattedAmount,
                    jackpotCurrency : b.jackpotCurrency,
                    amount : jp?.amount,
                    currency : jp?.currency
                })
            }
        }
    })

    return {
        banners : banners,
        bannerGames : bannerGames,
        jackpots : formattedJackpots
    }
})

export const getDefaultTabId = createSelector([getPageTabs], (tabs) => {
    return tabs.length > 0 ? tabs[0].id : undefined;
})
export const getDefaultPageStartTab = createSelector([getPages], (pages) => {
    return pages.find(p => p.defaultPage)?.tabs?.[0]?.id ?? undefined;
})


export const getFilterGamesForPromo = createSelector([],()=>{})

export interface CategoryGames {
    categoryId : number;
    maxNrOfGames?: number;
    games : CasinoGameInfo[];
}



export const _getBaseContent = (_ : AppState, content : BaseContent[]) => content;
export const selectGamesFromGameList = createSelector(
    [
        _getBaseContent,
        getCasinoState,
        getLoggedIn,
        getActiveCurrencyCode,
        getSettingsCountry,
        getCountry
    ],
    (
        baseContent,
        casino,
        loggedIn,
        activeCurrency,
        settingsCountry,
        country
    )=>{
        return getGamesFromGameList(baseContent, casino, loggedIn, activeCurrency, country ? country : settingsCountry);
})
/**
 * This method is not cached and should only be used together with caching/rendering improvements
 */
export const getGamesFromGameList = ( dynamicContent : BaseContent[], casino : CasinoState, loggedIn? : boolean, activeCurrency? : string, country? : string ) => {

    let selection : GameListSection[] = (dynamicContent ?? []).filter(dc => dc._contentType == DynamicContentEnum.GAMELISTSECTION)
        .filter((dc : GameListSection) => (dc.listOfGamePromo ?? []).length == 0 && typeof(dc.categoryId) != "undefined");

    let lobby = casino.lobby;
    if(!selection || selection.length == 0) {
        return [];
    }
    let categories :  CasinoLobbyCategory[] = selection.map( s =>  findCategory(lobby?.pages, s.categoryId) ?? { games : [] });
    let allGames = Object.values(lobby?.games ?? []);
    allGames = allGames.filter((g : CasinoGame) => {
        let currencies = lobby?.currencies ?? [];
        let casinoStudios = Object.values(lobby?.studios ?? {});
        return isGameAvailable(g, currencies, activeCurrency, loggedIn) && isGameAllowed(g, (casinoStudios ?? []).find(s => s.id === g.studioId), country);
    });
    let games : CasinoGame[][] = [];
    if(categories) {
        games =  extractGamesByCategory(categories, allGames, Device.isMobile());
        games = games.map( (arr, index ) => {
            if(arr.length > (selection[index].maxNrOfGames ?? 5)) {
                return arr.slice(0,selection[index].maxNrOfGames ?? 5);
            }
            return arr;
        })

    }
    let categoryGames = games.map((arr, index) => {
        return {
            categoryId : selection[index].categoryId,
            maxNrOfGames : selection[index].maxNrOfGames,
            games : arr
        } as CategoryGames
    });

    return categoryGames;
};